import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';

import {isElectron} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupDivider} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupDivider';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {TabButtonElement} from '../../analytics/components/tabs/tabButtonElement';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsNotificationsUrl,
  SettingsResourcePagesEnum,
  SettingsTabsPagesEnum,
} from '../../models/url/settingsUrlModel';
import {hasNotification, NotificationPermissionsEnum, setupNotifications} from '../../services/notifications';
import {getFindShouldDisplayInfoBox} from '../../store/data/localPreferences/localPreferencesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {StyledSettingsWrapper} from '../common/settingsWrapper';
import {SettingsHeader} from '../settingsHeader';
import {SettingsAdvancedNotificationsList} from './settingsAdvancedNotificationsList';
import {SettingsBasicNotifications} from './settingsBasicNotifications';
import {SettingsSingleAdvancedNotification} from './settingsSingleAdvancedNotification';

/*
 * Props.
 */

interface SettingsNotificationsContainerProps {
  panelId: SettingsPanelId;
  url: SettingsNotificationsUrl;
}

/*
 * Style.
 */

/*
 * Component.
 */

export const SettingsNotificationsContainer: FC<SettingsNotificationsContainerProps> = (props) => {
  const {navigateToLink} = useLinkNavigation();
  const findShouldDisplayInfoBox = useAppSelector(getFindShouldDisplayInfoBox);
  const isAdvancedView = props.url.tabPage === SettingsTabsPagesEnum.ADVANCED;
  const infoBoxId = isAdvancedView ? 'advanced_notifications' : 'basic_notifications';
  const shouldDisplayInfoBox = findShouldDisplayInfoBox(infoBoxId);
  const [isSidebarOpen, toggleSidebar] = useToggle(
    shouldDisplayInfoBox && props.url.resourcePage === SettingsResourcePagesEnum.OVERVIEW,
  );
  const [shouldShowEnableNotificationsButton, setShouldShowEnableNotificationsButton] = useState(
    () =>
      hasNotification() &&
      !props.url.namespace &&
      !isElectron() &&
      Notification?.permission !== NotificationPermissionsEnum.GRANTED,
  );

  const renderTabs = () => (
    <>
      <TabButtonElement
        interactionId="settingnotifications-tab-basic"
        name={<Trans id="settingnotifications-basic">Basic</Trans>}
        isSelected={props.url.tabPage === SettingsTabsPagesEnum.BASIC}
        onTabClick={() => {
          navigateToLink(
            buildSettingsPath({
              ...props.url,
              tabPage: SettingsTabsPagesEnum.BASIC,
            }),
          );
        }}
      />
      <TabButtonElement
        interactionId="settingnotifications-tab-advanced"
        name={<Trans id="settingnotifications-advanced">Advanced</Trans>}
        isSelected={props.url.tabPage === SettingsTabsPagesEnum.ADVANCED}
        onTabClick={() => {
          navigateToLink(
            buildSettingsPath({
              ...props.url,
              tabPage: SettingsTabsPagesEnum.ADVANCED,
            }),
          );
        }}
      />
    </>
  );

  const theme = useUserTheme();

  const renderHeaderRightButtons = () => {
    if (!shouldShowEnableNotificationsButton) {
      return (
        <IconButton
          iconName="info"
          label=""
          onClick={toggleSidebar}
          hasHover={isSidebarOpen}
          color={isSidebarOpen ? theme.greys.shade80 : theme.greys.shade70}
        />
      );
    }

    return (
      <ButtonGroup>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={async () => {
            const newPermission = await setupNotifications();
            if (newPermission === NotificationPermissionsEnum.GRANTED) {
              setShouldShowEnableNotificationsButton(false);
            }
          }}
          isDisabled={Notification?.permission === NotificationPermissionsEnum.DENIED}
          renderTooltip={() => {
            if (Notification?.permission !== NotificationPermissionsEnum.DENIED) {
              return null;
            }

            return (
              <Trans id="settingsnotifications-deniednotifications">
                Browser notifications were denied. You can modify this in your browser settings.
              </Trans>
            );
          }}
        >
          <Trans id="settingsnotifications-enablebrowsernotifications">Enable browser notifications</Trans>
        </Button>
        <ButtonGroupDivider />
        <IconButton
          iconName="info"
          label=""
          onClick={toggleSidebar}
          hasHover={isSidebarOpen}
          color={isSidebarOpen ? theme.greys.shade80 : theme.greys.shade70}
        />
      </ButtonGroup>
    );
  };

  if (
    props.url.resourcePage === SettingsResourcePagesEnum.EDIT ||
    props.url.resourcePage === SettingsResourcePagesEnum.NEW
  ) {
    return <SettingsSingleAdvancedNotification url={props.url} />;
  }

  return (
    <StyledSettingsWrapper>
      <SettingsHeader tabs={renderTabs()} right={renderHeaderRightButtons()}>
        <Trans id="settingsnotifications-notifications">Notifications</Trans>
      </SettingsHeader>

      {isAdvancedView ? (
        <SettingsAdvancedNotificationsList
          infoBoxId={infoBoxId}
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          url={props.url}
        />
      ) : (
        <SettingsBasicNotifications
          infoBoxId={infoBoxId}
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          url={props.url}
        />
      )}
    </StyledSettingsWrapper>
  );
};
